import type { VideoDetails, VideoMetadata } from '../values/VideoMetadata'
import type { SuccessResponse, UploadedUppyFile } from './UppyFile'

/**
 * Media metadata the upload service read from the stored object, in the shape
 * it is stored in (snake_case), the same one `/i/uploads/upload_media.json`
 * answered with.
 */
export type FinishedUploadDetails = Partial<VideoDetails> & {
  location: string
  /** Videos: the service scheduled a transcode for this asset. */
  required_transcoding?: boolean
  /** Documents: absent when the page count could not be read. */
  pageCount?: number
  fileSizeBytes?: number
}

/**
 * What the upload service reports about a finished upload, in the body of the
 * final `PATCH` — everything a client needs to attach the media, so no
 * follow-up call is made on this transport.
 *
 * @see services/uploads/src/finishedUpload.ts
 */
export type FinishedUpload = {
  uploadId: string
  /** Storage URL the bytes landed at. */
  location: string
  name: string
  type: 'photo' | 'video' | 'document'
  /** Absent while the record has no thumbnail yet. */
  thumbnail?: string
  details: FinishedUploadDetails
  /** Photos only, and `null` when the image could not be read. */
  width?: number | null
  height?: number | null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isNullableNumber(value: unknown): boolean {
  return value === undefined || value === null || typeof value === 'number'
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}

// An upload identified or located by an empty string is not usable: the media
// would be attached to a post under a URL that resolves to nothing.
export function isFinishedUpload(value: unknown): value is FinishedUpload {
  return (
    isRecord(value) &&
    isNonEmptyString(value.uploadId) &&
    isNonEmptyString(value.location) &&
    typeof value.name === 'string' &&
    (value.type === 'photo' ||
      value.type === 'video' ||
      value.type === 'document') &&
    (value.thumbnail === undefined || typeof value.thumbnail === 'string') &&
    isNullableNumber(value.width) &&
    isNullableNumber(value.height) &&
    isRecord(value.details) &&
    typeof value.details.location === 'string'
  )
}

/**
 * Reads the upload out of a finish-response body, or returns `undefined` for a
 * body that is not one — the empty 204 a plain TUS server answers with, or an
 * error page from something in between.
 */
export function parseFinishedUpload(body: string): FinishedUpload | undefined {
  if (!body) {
    return undefined
  }

  let parsed: unknown

  try {
    parsed = JSON.parse(body)
  } catch {
    return undefined
  }

  return isFinishedUpload(parsed) ? parsed : undefined
}

/**
 * The finished upload the service reported in a success response.
 *
 * `@uppy/tus` hands the raw `XMLHttpRequest` of the last request through as the
 * success body, which is the only place the finish response reaches the client:
 * the plugin parses no body itself, and `tus-js-client` reports success with a
 * response object the plugin does not forward.
 *
 * Returns `undefined` for an upload made over another transport.
 */
export function getFinishedUploadFromResponse(
  response: SuccessResponse,
): FinishedUpload | undefined {
  const { xhr } = response.body ?? {}

  if (!isRecord(xhr) || typeof xhr.responseText !== 'string') {
    return undefined
  }

  return parseFinishedUpload(xhr.responseText)
}

/** @see getFinishedUploadFromResponse */
export function getFinishedUpload(
  file: UploadedUppyFile,
): FinishedUpload | undefined {
  return getFinishedUploadFromResponse(file.response)
}

/**
 * Every field of the finish payload's video details is optional — the service
 * stores them that way, and probing a video it cannot read yields none of them.
 * `VideoMetadata.newFromDetails` takes the complete shape the legacy endpoint
 * answered with, so it cannot read this one.
 */
export function toVideoMetadata(details: FinishedUploadDetails): VideoMetadata {
  return {
    width: details.width ?? null,
    height: details.height ?? null,
    duration: details.duration ?? null,
    durationMs: details.duration_millis ?? null,
    frameRate: details.frame_rate ?? null,
    videoFormat: details.video_format ?? null,
    videoBitrate: details.video_bitrate ?? null,
    audioCodec: details.audio_codec ?? null,
    rotation: details.rotation ?? 0,
    format: details.format ?? null,
    transconding: details.required_transcoding ?? false,
  }
}
